// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-22) — what the headset's zoom (vrFocusZoom.ts) does
 * each frame around the controllers that change it:
 *
 * - keeps it under the ceiling for the place in focus, easing it down when
 *   the ceiling drops (the focus left a city pack, a fine dataset closed);
 * - tells the scene it is on, so the clouds hide;
 * - feeds the close-up tiles (vrDetailTiles.ts) the place in focus at the
 *   tile level the viewer needs;
 * - hides the geo-media dots, which do not follow it;
 * - shows the zoom card (vrZoomBadge.ts) with the level and the photos'
 *   credits.
 *
 * Vector layers and satellites read {@link isZoomed} where they are drawn.
 */

import type * as THREE from 'three'
import { t } from '../i18n'
import type { VrSceneHandle } from './vrScene'
import {
  commitFocusZoom,
  dirToLatLon,
  focusSource,
  focusZoomCeiling,
  getFocusZoom,
  isZoomed,
  zoomBy,
  zoomLevelOf,
} from './vrFocusZoom'
import { createVrDetailTiles, detailTileZoom, maxTileZoomAt, type VrDetailTilesHandle } from './vrDetailTiles'
import { createVrZoomBadge, type VrZoomBadgeHandle } from './vrZoomBadge'

/** How fast a zoom over a dropped ceiling comes down to it: e-fold per second. */
const SETTLE_RATE = 4

export interface VrFocusZoomViewHandle {
  /** How far in the right stick may zoom now, as a magnification. */
  ceiling(): number
  /** Once a frame, after the controllers and before `scene.update()`. */
  update(camera: THREE.Camera, nowMs: number, deltaSeconds: number, fineData: boolean): void
  dispose(): void
}

export function createVrFocusZoomView(
  THREE_: typeof THREE,
  scene: VrSceneHandle,
  globeRadius: number,
  givenTiles?: VrDetailTilesHandle,
  badge: VrZoomBadgeHandle = createVrZoomBadge(THREE_),
): VrFocusZoomViewHandle {
  // AYNI (Quest review D7, 2026-11-03): the close-up tiles (three 2048² canvases
  // and their textures, ~48 MiB + ~67 MiB) are made on the first zoom, not on
  // every entry for a session that may never zoom.
  let tiles: VrDetailTilesHandle | null = givenTiles ?? null
  const zoomTiles = (): VrDetailTilesHandle => (tiles ??= createVrDetailTiles(THREE_))
  scene.scene.add(badge.mesh)
  let ceiling = focusZoomCeiling(0, 0, false)
  const fold = new THREE_.Quaternion()
  const focusWorld = new THREE_.Vector3()
  const centre = new THREE_.Vector3()
  const cam = new THREE_.Vector3()

  return {
    ceiling: () => ceiling,

    update(camera, nowMs, deltaSeconds, fineData) {
      const zoom = getFocusZoom()
      const globe = scene.globe
      if (isZoomed(zoom)) {
        const place = dirToLatLon(focusSource(zoom))
        ceiling = focusZoomCeiling(place.lat, place.lon, fineData)
        if (zoom.m > ceiling) {
          const back = zoomBy(zoom, Math.max(ceiling / zoom.m, Math.exp(-SETTLE_RATE * deltaSeconds)), null, ceiling)
          if (back) globe.quaternion.multiply(fold.set(back[0], back[1], back[2], back[3]))
          commitFocusZoom()
        }
      }
      const zoomed = isZoomed(zoom)
      scene.setZoomed(zoomed)
      for (const child of globe.children) if (child.name === 'geoMediaDots') child.visible = !zoomed

      globe.updateMatrixWorld()
      globe.getWorldPosition(centre)
      const worldRadius = globeRadius * globe.scale.x
      if (!zoomed) {
        tiles?.update(null, nowMs)
        badge.set(null)
        return
      }
      const place = dirToLatLon(focusSource(zoom))
      focusWorld.set(zoom.fd[0] * globeRadius, zoom.fd[1] * globeRadius, zoom.fd[2] * globeRadius)
      globe.localToWorld(focusWorld)
      camera.getWorldPosition(cam)
      const tileZoom = Math.min(
        detailTileZoom(place.lat, zoom.m, worldRadius, cam.distanceTo(focusWorld)),
        maxTileZoomAt(place.lat, place.lon),
      )
      zoomTiles().update({ lat: place.lat, lon: place.lon, tileZoom }, nowMs)

      const level = zoomLevelOf(zoom.m).toFixed(1)
      const atCeiling = zoom.m >= ceiling * 0.999
      badge.set({
        title: atCeiling ? `${t('vr.zoom.level', { level })} · ${t('vr.zoom.closest')}` : t('vr.zoom.level', { level }),
        credit: zoomTiles().credits().join(' · '),
      })
      badge.place(camera, centre, worldRadius)
    },

    dispose() {
      tiles?.dispose()
      scene.scene.remove(badge.mesh)
      badge.dispose()
    },
  }
}
